import { PopupCustom, PopupSuccess } from "@components/generals/popup";
import { Spiner, SpinerPage } from "@components/loaders/spiner";
import {
  Bills,
  DaftarZiswaf,
  PaymentListSpp,
} from "@services/dtos/finance.dto";
import { FinanceService } from "@services/finance.service";
import { SchoolService } from "@services/school.service";
import { useNotificationStore } from "@stores/notification.store";
import { useThemeStore } from "@stores/theme.store";
import { useMutation, useQuery } from "@tanstack/react-query";
import { formatValue, toRp } from "@utils/helper";
import { linkPage } from "@utils/router";
import moment from "moment";
import lodash from "lodash";
import { useEffect, useState } from "react";
import { FaChevronDown, FaFileInvoiceDollar } from "react-icons/fa";
import { FcFinePrint } from "react-icons/fc";
import { useLocation, useNavigate } from "react-router-dom";

export const VirtualAccountsRequestPage = () => {
  const navigate = useNavigate();
  const { state } = useLocation();
  const { pushNotification } = useNotificationStore((state) => state);
  const { setFooter, setHeader, setAppBar } = useThemeStore((state) => state);
  const [popupPayment, setPopupPayment] = useState<boolean>(false);
  const [popupSuccess, setPopupSuccess] = useState<boolean>(false);
  const [result, setResult] = useState<any>({});

  // const [paymentMethod, setPaymentMethod] = useState<"va" | "saving">("va");
  const [paymentBank, setPaymentBank] = useState<"BNI" | "BSI">("BNI");
  const [pocket, setPocket] = useState<string>("UANGSAKU");
  const [pocketNominal, setPocketNominal] = useState<string>("");
  const [infaqNominal, setInfaqNominal] = useState<string>("");
  const [bimbelNominal, setBimbelNominal] = useState<string>("");
  const [bills, setBills] = useState<
    { id: string; notes: string; amount: number; type: string }[]
  >([]);
  const [billZiswaf, setBillZiswaf] = useState<
    {
      id: string;
      keterangan: string;
      nominal: number;
      batasWaktu: Date;
      mode: string;
      opsi: string;
    }[]
  >([]);
  const [idxDisabledPayment, setIdxDisabledPayment] = useState<
    { index: number; id: string; amount: number }[]
  >([]);
  const adminFee = 3000;

  const { data: bio } = useQuery(
    [SchoolService.queries.GET_SCHOOL_STUDENT_BIO],
    SchoolService.studentBio,
  );
  const nis = bio?.data?.nis ?? "";

  const { data, isLoading } = useQuery(
    [FinanceService.queries.GET_FINANCE_BILLS_PAID_OF, nis],
    () => FinanceService.billsPaidOf({ nis }),
  );

  const { data: dataZiswaf, isLoading: isLoadingZiswaf } = useQuery(
    [FinanceService.queries.GET_ZISWAF],
    () => FinanceService.ziswafList(),
  );

  const activeZiswaf = dataZiswaf?.data?.filter(
    (item) =>
      item.batasWaktu === "0000-00-00" ||
      moment(item.batasWaktu, "YYYY-MM-DD").isSameOrAfter(moment(), "day"),
  );

  const { data: dataMarhalah, isLoading: isLoadingMarhalah } = useQuery(
    [SchoolService.queries.GET_SCHOOL_STUDENT_MARHALAH, nis],
    () => SchoolService.studentMarhalah({ nis }),
  );

  const { mutate: mutateVA, isLoading: isLoadingPaymentVa } = useMutation(
    FinanceService.virtualAccountRequest,
    {
      onSuccess: (response) => {
        if (response.success) {
          setResult(response.data);
          setPopupPayment(false);
          setPopupSuccess(true);
        } else {
          pushNotification({
            type: "error",
            message: response.message,
          });
        }
      },
      onError: (error: any) => {
        pushNotification({
          type: "error",
          message: error.message ?? "Internal Error",
        });
      },
    },
  );

  const { mutate: mutateZiswafVA, isLoading: isLoadingPaymentZiswaf } =
    useMutation({
      mutationFn: FinanceService.virtualAccountRequestZiswaf,
      onSuccess: (response) => {
        if (response.success) {
          setResult(response.data);
          setPopupPayment(false);
          setPopupSuccess(true);
        } else {
          pushNotification({
            type: "error",
            message: response.message,
          });
        }
      },
      onError: (error: any) => {
        pushNotification({
          type: "error",
          message: error.message ?? "Internal Error",
        });
      },
    });

  useEffect(() => {
    setFooter(false);
    setHeader({
      type: "small",
      title: state?.title,
      buttonBack: true,
      avatar: true,
    });
    setAppBar({ leading: "back", title: "Request VA" });
  }, [setFooter, setHeader, setAppBar]);

  const onPayment = () => {
    const nominal = pocketNominal.replace(/,/g, "");
    const bimbel = bimbelNominal === "" ? 0 : parseInt(bimbelNominal);
    const infaq = infaqNominal === "" ? 0 : parseInt(infaqNominal);
    if (
      state.tagihan &&
      pocketNominal === "" &&
      bills.length < 1 &&
      bimbel === 0 &&
      infaq === 0
    ) {
      pushNotification({
        type: "error",
        message: "Nominal atau pembayaran harus ada yang diisi",
      });
      return;
    }
    if (billZiswaf.length < 1 && !state.tagihan) {
      pushNotification({
        type: "error",
        message: "Nominal harus ada yang diisi",
      });
      return;
    }
    if (
      bills.map((i) => i.amount).reduce((a, b) => a + b, 0) +
        parseInt(nominal) <
      10000
    ) {
      pushNotification({
        type: "error",
        message: "Nominal top-up Uang Saku minimal Rp 10.000",
      });
      return;
    }
    setPocketNominal(pocketNominal.replace(/,/g, ""));
    setPopupPayment(true);
  };

  const onConfirmPayment = () => {
    const sppValues =
      bills
        .filter((item) => item.type === "bulanan")
        .map((item) => item.notes)
        .join(":") || "";

    if (state.tagihan) {
      mutateVA({
        nis: nis,
        bank: paymentBank,
        data: {
          bimbel:
            bimbelNominal === ""
              ? 0
              : parseInt(bimbelNominal.replace(/\./g, "")),
          daftarulang:
            bills.find((item) => item.notes.toLowerCase() === "daftar ulang")
              ?.amount ?? 0,
          infaq:
            infaqNominal === "" ? 0 : parseInt(infaqNominal.replace(/\./g, "")),
          uangpangkal:
            bills.find((item) => item.notes.toLowerCase() === "uang pangkal")
              ?.amount ?? 0,
          uangsakumasuk:
            pocketNominal === ""
              ? 0
              : parseInt(pocketNominal.replace(/\./g, "")),
          spp: sppValues as Partial<PaymentListSpp>,
        },
      });
    } else if (!state.tagihan) {
      mutateZiswafVA({
        nis: nis,
        id_rekening: paymentBank,
        data_ziswaf: billZiswaf.map((item) => ({
          id_ziswaf: item.id,
          nominal: item.nominal,
        })),
      });
    }
  };

  const onChangeBill = (val: string, bill: Bills) => {
    const current = bills.filter((item) => item.id !== bill.nourut);

    if (bill.jenis === "bulanan") {
      onDisabledPayment(val, bill.nourut, bill.jenis);
    }

    if (val === "") {
      bill.jenis === "bulanan"
        ? onDisabledPayment(val, bill.nourut, bill.jenis)
        : setBills(current);

      return;
    }
    setBills(
      current.concat({
        id: bill.nourut,
        notes: `${bill.tagihan}`,
        amount: parseInt(val),
        type: bill.jenis,
      }),
    );
  };

  const onChangeBillZiswaf = (val: string, dataZiswaf: DaftarZiswaf) => {
    let nominal = 0;
    const current = billZiswaf.filter((item) => item.id !== dataZiswaf.id);

    if (val === "") {
      setBillZiswaf(current);
      return;
    }
    nominal += parseInt(val);

    setBillZiswaf(
      current.concat({
        id: dataZiswaf.id,
        keterangan: dataZiswaf.keterangan,
        nominal: nominal,
        batasWaktu: moment(dataZiswaf.batasWaktu, "YYYY-MM-DD").toDate(),
        mode: dataZiswaf.mode,
        opsi: dataZiswaf.opsi,
      }),
    );
  };

  const checkData =
    data?.data
      ?.filter((item) => item.jenis === "bulanan")
      .map((item, index) => ({
        index: index,
        id: item.nourut,
        amount: item.nominal,
      })) ?? [];

  const onDisabledPayment = (val: string, nourut: string, tipe: string) => {
    const nourutIndex = checkData.findIndex((item) => item.id === nourut);
    console.log(nourutIndex);

    if (
      val !== "" &&
      nourutIndex !== -1 &&
      nourutIndex < checkData.length - 1
    ) {
      const dataDisabled = [...idxDisabledPayment, checkData[nourutIndex + 1]];
      const uniqueElements = lodash.uniqBy(dataDisabled, "id");

      setIdxDisabledPayment(uniqueElements);
    } else if (val === "" || val === null || val === undefined) {
      const updatedData = idxDisabledPayment.filter(
        (item) => item.index <= nourutIndex,
      );
      const billsIndex = checkData.findIndex((item) => item.id === nourut);

      if (billsIndex !== -1 && billsIndex < bills.length) {
        const updatedBills = bills.filter((item, index) => {
          return index < billsIndex || !(item.type === "bulanan");
        });
        setBills(updatedBills);
      }
      console.log(updatedData);

      setIdxDisabledPayment(updatedData);
    }
  };

  const checkFirstIndexArr = (nourut: string) => {
    const currentArr = data?.data?.filter((item) => item.jenis === "bulanan");
    return (
      currentArr && currentArr.length > 0 && currentArr[0].nourut === nourut
    );
  };

  const lainLain = [
    { label: "Kegiatan Asrama", value: bimbelNominal },
    { label: "Infaq", value: infaqNominal },
  ].filter((item) => item.value !== "");
  const lainLainTotal = lainLain.reduce(
    (total, item) => total + parseInt(item.value),
    0,
  );

  useEffect(() => {
    if (checkData.length > 0) {
      setIdxDisabledPayment([
        {
          index: checkData[0].index,
          id: checkData[0].id,
          amount: checkData[0].amount,
        },
      ]);
    }
  }, [data]);

  return (
    <>
      <SpinerPage show={isLoadingPaymentVa} />
      {state.tagihan && (
        <>
          {/* {paymentMethod === "va" && ( */}
          <div className="card card-style !tw-mb-4">
            <div className="content mb-0 mt-2">
              <h5 className="mb-3">Uang Saku</h5>
              <div className="input-style has-borders hnoas-icon input-style-always-active validate-field mb-4">
                <label htmlFor="form5" className="color-highlight font-500">
                  Nominal Top-up
                </label>
                <input
                  type="text"
                  className="form-control bg-theme"
                  placeholder="Rp."
                  inputMode="numeric"
                  onChange={(e) => {
                    const value = e.target.value.replace(/\D/g, "");
                    e.target.value = formatValue(value);
                    setPocketNominal(value);
                  }}
                  onKeyDown={(event) => {
                    if (!/[0-9]|Backspace|Tab/.test(event.key)) {
                      event.preventDefault();
                    }
                  }}
                />
              </div>
            </div>
          </div>
          {/* )} */}

          {/* lain-lain */}
          <div className="card card-style !tw-mb-4">
            <div className="content my-2">
              <h5>Lain - Lain</h5>
              <div className="list-group list-group-flush">
                <div className="list-group-item tw-bg-inherit d-flex tw-px-0 tw-py-3 tw-border-[rgba(0,0,0,0.05)] dark:tw-border-[rgba(255,255,255,0.05)] tw-cursor-pointer">
                  <div className="rounded-sm tw-w-8 tw-h-8 d-flex tw-flex-none my-auto bg-magenta-dark">
                    <FaFileInvoiceDollar className="m-auto" size={18} />
                  </div>
                  <div className="tw-grow mx-3 my-auto">
                    <div className="tw-font-medium tw-text-sm dark:tw-text-white tw-truncate tw-w-[calc(100vw-240px)]">
                      Kegiatan Asrama
                    </div>
                    <div className="tw-font-medium tw-text-[11px] tw-leading-4 tw-text-[#adb5bd] tw-truncate tw-line-clamp-1"></div>
                  </div>
                  <div className="tw-w-[140px]">
                    <div className="input-style bills has-borders hnoas-icon input-style-always-active validate-field">
                      <input
                        type="text"
                        className="form-control bg-theme"
                        placeholder="Rp."
                        inputMode="numeric"
                        onChange={(e) => {
                          const value = e.target.value.replace(/\D/g, "");
                          e.target.value = formatValue(value);
                          setBimbelNominal(value);
                        }}
                        onKeyDown={(event) => {
                          if (!/[0-9]|Backspace|Tab/.test(event.key)) {
                            event.preventDefault();
                          }
                        }}
                      />
                      <label
                        htmlFor="form1"
                        className="color-highlight font-400 font-13"
                      >
                        Nominal
                      </label>
                    </div>
                  </div>
                </div>

                <div className="list-group-item tw-bg-inherit d-flex tw-px-0 tw-py-3 tw-border-[rgba(0,0,0,0.05)] dark:tw-border-[rgba(255,255,255,0.05)] tw-cursor-pointer">
                  <div className="rounded-sm tw-w-8 tw-h-8 d-flex tw-flex-none my-auto bg-magenta-dark">
                    <FaFileInvoiceDollar className="m-auto" size={18} />
                  </div>
                  <div className="tw-grow mx-3 my-auto">
                    <div className="tw-font-medium tw-text-sm dark:tw-text-white tw-truncate tw-w-[calc(100vw-240px)]">
                      Infaq
                    </div>
                    <div className="tw-font-medium tw-text-[11px] tw-leading-4 tw-text-[#adb5bd] tw-truncate tw-line-clamp-1"></div>
                  </div>
                  <div className="tw-w-[140px]">
                    <div className="input-style bills has-borders hnoas-icon input-style-always-active validate-field">
                      <input
                        type="text"
                        className="form-control bg-theme"
                        placeholder="Rp."
                        inputMode="numeric"
                        onChange={(e) => {
                          const value = e.target.value.replace(/\D/g, "");
                          e.target.value = formatValue(value);
                          setInfaqNominal(value);
                        }}
                        onKeyDown={(event) => {
                          if (!/[0-9]|Backspace|Tab/.test(event.key)) {
                            event.preventDefault();
                          }
                        }}
                      />
                      <label
                        htmlFor="form1"
                        className="color-highlight font-400 font-13"
                      >
                        Nominal
                      </label>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* tagihan bulanan */}
          <div className="card card-style !tw-mb-4">
            <div className="content my-2">
              <h5 className="mb-0">Daftar Tagihan Bulanan</h5>
              <small>*isi dari urutan paling atas</small>
              {isLoading ? (
                <div className="d-flex p-3">
                  <Spiner className="!tw-text-gray-200 tw-fill-gray-500 dark:tw-fill-white dark:!tw-text-gray-600 tw-w-8 tw-h-8 m-auto " />
                </div>
              ) : data?.data != null &&
                data.data.length > 0 &&
                data.data.filter((item) => item.jenis === "bulanan").length >
                  0 ? (
                <div className="list-group list-group-flush">
                  {data.data
                    .filter((item) => item.jenis === "bulanan")
                    .map((item, idx) => (
                      <div
                        key={idx}
                        className="list-group-item tw-bg-inherit d-flex tw-px-0 tw-py-3 tw-border-[rgba(0,0,0,0.05)] dark:tw-border-[rgba(255,255,255,0.05)] tw-cursor-pointer"
                      >
                        <div className="rounded-sm tw-w-8 tw-h-8 d-flex tw-flex-none my-auto bg-magenta-dark">
                          <FaFileInvoiceDollar className="m-auto" size={18} />
                        </div>
                        <div className="tw-grow mx-3 my-auto">
                          <div className="tw-font-medium tw-text-sm dark:tw-text-white tw-truncate tw-w-[calc(100vw-240px)]">
                            {item.tagihan}
                          </div>
                          <div className="tw-font-medium tw-text-[11px] tw-leading-4 tw-text-[#adb5bd] tw-truncate tw-line-clamp-1">
                            Total : {toRp(item.nominal ?? 0)}
                          </div>
                        </div>
                        <div className="tw-w-[140px]">
                          <div className="input-style bills has-borders input-style-always-active no-icon">
                            <label
                              htmlFor="form5"
                              className="color-highlight font-500"
                            >
                              Nominal
                            </label>
                            <input
                              type="text"
                              className="form-control bg-theme"
                              placeholder="Rp."
                              inputMode="numeric"
                              value={
                                idxDisabledPayment.some(
                                  (disabledItem) =>
                                    disabledItem.id === item.nourut,
                                )
                                  ? formatValue(
                                      (
                                        bills.find((i) => i.id === item.nourut)
                                          ?.amount ?? ""
                                      )
                                        ?.toString()
                                        .replace(/\D/g, ""),
                                    )
                                  : ""
                              }
                              onClick={(e) => {
                                const input = e.target as HTMLInputElement;
                                const value = item.nominal
                                  .toString()
                                  .replace(/\D/g, "");
                                input.value =
                                  input.value === "" ? formatValue(value) : "";
                                onChangeBill(
                                  input.value.toString().replace(/\D/g, ""),
                                  item,
                                );
                              }}
                              disabled={
                                (checkFirstIndexArr(item.nourut) &&
                                  idxDisabledPayment.length === 0) ||
                                idxDisabledPayment.some(
                                  (disabledItem) =>
                                    disabledItem.id === item.nourut,
                                )
                                  ? false
                                  : true
                              }
                              readOnly
                            />
                          </div>
                        </div>
                      </div>
                    ))}
                </div>
              ) : (
                <div className="tw-p-8 text-center ">
                  <FcFinePrint size={50} />
                  <div>Tidak ada tagihan</div>
                </div>
              )}
            </div>
          </div>

          {/* tagihan sekali */}
          <div className="card card-style !tw-mb-4">
            <div className="content my-2">
              <h5>Daftar Tagihan Non Bulanan</h5>
              {isLoading ? (
                <div className="d-flex p-3">
                  <Spiner className="!tw-text-gray-200 tw-fill-gray-500 dark:tw-fill-white dark:!tw-text-gray-600 tw-w-8 tw-h-8 m-auto " />
                </div>
              ) : data?.data != null &&
                data.data.length > 0 &&
                data.data.filter((item) => item.jenis !== "bulanan").length >
                  0 ? (
                <div className="list-group list-group-flush">
                  {data.data
                    .filter((item) => item.jenis !== "bulanan")
                    .map((item, idx) => (
                      <div
                        key={idx}
                        className="list-group-item tw-bg-inherit d-flex tw-px-0 tw-py-3 tw-border-[rgba(0,0,0,0.05)] dark:tw-border-[rgba(255,255,255,0.05)] tw-cursor-pointer"
                      >
                        <div className="rounded-sm tw-w-8 tw-h-8 d-flex tw-flex-none my-auto bg-magenta-dark">
                          <FaFileInvoiceDollar className="m-auto" size={18} />
                        </div>
                        <div className="tw-grow mx-3 my-auto">
                          <div className="tw-font-medium tw-text-sm dark:tw-text-white tw-truncate tw-w-[calc(100vw-240px)]">
                            {item.tagihan}
                          </div>
                          <div className="tw-font-medium tw-text-[11px] tw-leading-4 tw-text-[#adb5bd] tw-truncate tw-line-clamp-1">
                            Total : {toRp(item.nominal ?? 0)}
                          </div>
                        </div>
                        <div className="tw-w-[140px]">
                          <div className="input-style bills has-borders hnoas-icon input-style-always-active validate-field">
                            <input
                              type="text"
                              className="form-control bg-theme"
                              placeholder="Rp."
                              inputMode="numeric"
                              onChange={(e) => {
                                const rawValue = e.target.value.replace(
                                  /\D/g,
                                  "",
                                );
                                const maxValue = item.nominal ?? 0;
                                const value =
                                  rawValue !== "" &&
                                  parseInt(rawValue) > maxValue
                                    ? maxValue.toString()
                                    : rawValue;
                                e.target.value = formatValue(value);
                                onChangeBill(value, item);
                              }}
                              onKeyDown={(event) => {
                                if (!/[0-9]|Backspace|Tab/.test(event.key)) {
                                  event.preventDefault();
                                }
                              }}
                            />
                            <label
                              htmlFor="form1"
                              className="color-highlight font-400 font-13"
                            >
                              Nominal
                            </label>
                          </div>
                        </div>
                      </div>
                    ))}
                </div>
              ) : (
                <div className="tw-p-8 text-center ">
                  <FcFinePrint size={50} />
                  <div>Tidak ada tagihan</div>
                </div>
              )}
            </div>
          </div>
        </>
      )}

      {!state.tagihan && (
        <div className="card card-style !tw-mb-4">
          <div className="content my-2">
            {isLoadingZiswaf ? (
              <div className="d-flex p-3">
                <Spiner className="!tw-text-gray-200 tw-fill-gray-500 dark:tw-fill-white dark:!tw-text-gray-600 tw-w-8 tw-h-8 m-auto " />
              </div>
            ) : activeZiswaf != null && activeZiswaf.length > 0 ? (
              <div className="list-group list-group-flush">
                {activeZiswaf.map((item, idx) => (
                  <div
                    key={idx}
                    className="list-group-item tw-bg-inherit d-flex tw-px-0 tw-py-3 tw-border-[rgba(0,0,0,0.05)] dark:tw-border-[rgba(255,255,255,0.05)] tw-cursor-pointer"
                  >
                    <div className="rounded-sm tw-w-8 tw-h-8 d-flex tw-flex-none my-auto bg-magenta-dark">
                      <FaFileInvoiceDollar className="m-auto" size={18} />
                    </div>
                    <div className="tw-grow mx-3 my-auto">
                      <div className="tw-font-medium tw-text-sm dark:tw-text-white tw-truncate tw-w-[calc(100vw-240px)]">
                        {item.keterangan}
                      </div>
                      <div className="tw-font-medium tw-text-[11px] tw-leading-4 tw-text-[#adb5bd] tw-truncate tw-line-clamp-1">
                        {item.mode === "SEMENTARA" && (
                          <div className="tw-font-medium tw-text-[11px] tw-leading-4 tw-text-[#adb5bd] tw-truncate tw-line-clamp-1">
                            s/d:{" "}
                            {moment(item.batasWaktu).format("DD MMMM YYYY")}
                          </div>
                        )}
                      </div>
                    </div>
                    <div className="tw-w-[140px]">
                      <div className="input-style bills has-borders input-style-always-active no-icon">
                        {item.nominal <= 0 ? (
                          <input
                            type="text"
                            className="form-control bg-theme"
                            placeholder="Nominal"
                            inputMode="numeric"
                            onChange={(e) => {
                              const value = e.target.value.replace(/\D/g, "");
                              e.target.value = formatValue(value);
                              onChangeBillZiswaf(value, item);
                            }}
                            onKeyDown={(event) => {
                              if (!/[0-9]|Backspace|Tab/.test(event.key)) {
                                event.preventDefault();
                              }
                            }}
                          />
                        ) : (
                          <input
                            type="text"
                            className="form-control bg-theme"
                            placeholder="Nominal"
                            inputMode="numeric"
                            value={formatValue(
                              (
                                billZiswaf.find((i) => i.id === item.id)
                                  ?.nominal ?? ""
                              )
                                ?.toString()
                                .replace(/\D/g, ""),
                            )}
                            onClick={(e) => {
                              const input = e.target as HTMLInputElement;
                              const value = item.nominal
                                .toString()
                                .replace(/\D/g, "");
                              input.value =
                                input.value === "" ? formatValue(value) : "";
                              onChangeBillZiswaf(
                                input.value.toString().replace(/\D/g, ""),
                                item,
                              );
                            }}
                            readOnly
                          />
                        )}
                        <label
                          htmlFor="form1"
                          className="color-highlight font-400 font-13"
                        >
                          {item.nominal > 0 ? toRp(item.nominal) : "∞"}
                        </label>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="tw-p-8 text-center ">
                <FcFinePrint size={50} />
                <div>Tidak ada ziswaf</div>
              </div>
            )}
          </div>
        </div>
      )}

      {/* {paymentMethod === "va" && ( */}
      <div className="card card-style !tw-mb-4">
        <div className="content mb-0">
          <div className="input-style has-borders input-style-always-active no-icon tw-grow">
            <label htmlFor="form5" className="color-highlight font-500">
              Pilih Bank
            </label>
            <select
              defaultValue={paymentBank}
              className="bg-theme"
              onChange={(e) => setPaymentBank(e.target.value as "BNI" | "BSI")}
            >
              <option value="default" disabled>
                Pilih Bank
              </option>
              <option value="BNI">Bank Negara Indonesia (BNI)</option>
              {dataMarhalah?.data?.marhalah !== "SMA" && (
                <option value="BSI">Bank Syariah Indonesia (BSI)</option>
              )}
            </select>
            <span>
              <i>
                <FaChevronDown />
              </i>
            </span>
          </div>
        </div>
      </div>
      {/* )} */}

      <div className="card card-style !tw-mb-4">
        <div className="content">
          <button
            className="btn btn-sm btn-full rounded-xs text-uppercase font-900 shadow-s bg-blue-dark tw-grow tw-w-full tw-h-11"
            onClick={onPayment}
          >
            Lakukan Pembayaran
          </button>
        </div>
      </div>

      <PopupCustom
        show={popupPayment}
        position="bottom"
        height={320}
        onCancel={() => setPopupPayment(false)}
      >
        <div className="pb-2 px-3">
          <h4 className="font-700 mt-3">Konfirmasi Pembayaran</h4>
          <div className="divider mt-3 mb-3"></div>
          {state.tagihan && (
            <>
              <div className="tw-font-bold">Topup</div>
              <div className="tw-flex">
                <div className="tw-grow">
                  {pocket === "UANGSAKU" ? "Uang Saku" : ""}
                </div>
                <div>
                  {toRp(pocketNominal === "" ? 0 : parseInt(pocketNominal))}
                </div>
              </div>
            </>
          )}
          {bills.length > 0 ? (
            <>
              <div className="tw-font-bold mt-2">Tagihan</div>
              {bills.map((item, idx) => (
                <div key={idx} className="tw-flex">
                  <div className="tw-grow">{item.notes}</div>
                  <div>{toRp(item.amount)}</div>
                </div>
              ))}
            </>
          ) : null}
          {billZiswaf.length > 0 ? (
            <>
              <div className="tw-font-bold mt-2">Ziswaf</div>
              {billZiswaf.map((item, idx) => (
                <div key={idx} className="tw-flex">
                  <div className="tw-grow">{item.keterangan}</div>
                  <div>{toRp(item.nominal)}</div>
                </div>
              ))}
            </>
          ) : null}
          {state.tagihan && lainLain.length > 0 ? (
            <>
              <div className="tw-font-bold mt-2">Lain - Lain</div>
              {lainLain.map((item, idx) => (
                <div key={idx} className="tw-flex">
                  <div className="tw-grow">{item.label}</div>
                  <div>{toRp(parseInt(item.value))}</div>
                </div>
              ))}
            </>
          ) : null}
          <div className="divider !tw-my-2" />
          <div className="tw-flex">
            <div className="tw-grow">Subtotal</div>
            <div>
              {state.tagihan &&
                toRp(
                  bills.map((i) => i.amount).reduce((a, b) => a + b, 0) +
                    (pocketNominal === "" ? 0 : parseInt(pocketNominal)) +
                    lainLainTotal,
                )}
              {!state.tagihan &&
                toRp(
                  billZiswaf.map((i) => i.nominal).reduce((a, b) => a + b, 0),
                )}
            </div>
          </div>
          {/* {paymentMethod === "va" && ( */}
          <div className="tw-flex">
            <div className="tw-grow">Biaya Admin</div>
            <div>{toRp(adminFee)}</div>
          </div>
          {/* )} */}
          <div className="tw-flex">
            <div className="tw-grow tw-font-bold">Total</div>
            <div className="tw-font-bold">
              {state.tagihan &&
                toRp(
                  bills.map((i) => i.amount).reduce((a, b) => a + b, 0) +
                    (pocketNominal === "" ? 0 : parseInt(pocketNominal)) +
                    lainLainTotal +
                    // (paymentMethod === "va" ? adminFee : 0)
                    adminFee,
                )}
              {!state.tagihan &&
                toRp(
                  billZiswaf.map((i) => i.nominal).reduce((a, b) => a + b, 0) +
                    adminFee,
                )}
            </div>
          </div>
          <div className="divider !tw-my-2" />
          <button
            type="submit"
            className="btn btn-sm btn-full rounded-xs text-uppercase font-900 shadow-s bg-blue-dark tw-grow my-3 tw-w-full tw-h-11"
            onClick={onConfirmPayment}
          >
            Konfirmasi Bayar (
            {state.tagihan &&
              toRp(
                bills.map((i) => i.amount).reduce((a, b) => a + b, 0) +
                  (pocketNominal === "" ? 0 : parseInt(pocketNominal)) +
                  lainLainTotal +
                  // (paymentMethod === "va" ? adminFee : 0)
                  adminFee,
              )}
            {!state.tagihan &&
              toRp(
                billZiswaf.map((i) => i.nominal).reduce((a, b) => a + b, 0) +
                  adminFee,
              )}
            )
          </button>
        </div>
      </PopupCustom>

      <PopupSuccess
        show={popupSuccess}
        title="Berhasil!"
        message={
          // paymentMethod === "va"
          "Permintaan virtual account (VA) berhasil dibuat!"
          // : "BERHASIL, Anda telah melakukan mutasi / pembayaran melalui TABUNGAN"
        }
        // textButton={paymentMethod === "va" ? "Lihat Detail" : "Tutup"}
        textButton="Lihat Detail"
        onConfirm={
          () =>
            // paymentMethod === "va"
            navigate(linkPage.FINANCE_VIRTUAL_ACCOUNT_REPORT_DETAIL, {
              replace: true,
              state: {
                vaBankFee: adminFee,
                vaBankName: paymentBank,
                vaDateExpired: result.datetime_expired,
                vaDatePayment: null,
                vaName: result.customer_name,
                vaNumber: result.virtual_account,
                vaStatus: "PENDING",
                vaTrxAmount: parseInt(result.trx_amount),
                vaTrxId: result.trx_id,
              },
            })
          // : navigate(-1)
        }
      />
    </>
  );
};
